/** Agent 可选图标目录：YAML theme.icon 引用此处 id；未配置时用 default。
 *  image 类图标按 primary 动态生成，与主题色统一。
 */

export type AgentIconKind = 'glyph' | 'image';

export interface AgentIconDefinition {
  id: string;
  /** 展示名（配置说明 / 运营可选列表） */
  name: string;
  /** 一句话说明 */
  description: string;
  kind: AgentIconKind;
  /** kind=glyph 时使用 */
  glyph?: string;
}

/** 从主题色派生的图标调色板（浅底 / 深底各一套） */
export interface IconPalette {
  primary: string;
  /** 浅底：主体渐变 */
  lightG1: string;
  lightG2: string;
  lightG3: string;
  lightAccent: string;
  lightAccentAlt: string;
  lightBadgeMark: string;
  lightShadow: string;
  /** 深底：描边 / 光晕渐变 */
  darkG1: string;
  darkG2: string;
  darkG3: string;
  darkAccent: string;
  darkAccentAlt: string;
  darkBadge1: string;
  darkBadge2: string;
  darkBadgeMark: string;
}

const DEFAULT_ICON_PRIMARY = '#00E5FF';

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function normalizeHex(color: string | null | undefined): string | null {
  if (!color) return null;
  const raw = color.trim();
  const m3 = /^#?([0-9a-fA-F]{3})$/.exec(raw);
  if (m3) {
    const [a, b, c] = m3[1];
    return `#${a}${a}${b}${b}${c}${c}`.toUpperCase();
  }
  const m6 = /^#?([0-9a-fA-F]{6})$/.exec(raw);
  if (m6) return `#${m6[1]}`.toUpperCase();
  return null;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const norm = normalizeHex(hex);
  if (!norm) return null;
  const n = parseInt(norm.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const hh = ((h % 360) + 360) % 360;
  const ss = clamp01(s);
  const ll = clamp01(l);
  if (ss === 0) {
    const v = ll * 255;
    return rgbToHex(v, v, v);
  }
  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
  const p = 2 * ll - q;
  const hk = hh / 360;
  const channel = (t: number) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return rgbToHex(channel(hk + 1 / 3) * 255, channel(hk) * 255, channel(hk - 1 / 3) * 255);
}

function mixHex(a: string, b: string, t: number): string {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  if (!A || !B) return a;
  const k = clamp01(t);
  return rgbToHex(A.r + (B.r - A.r) * k, A.g + (B.g - A.g) * k, A.b + (B.b - A.b) * k);
}

/** 从主题色派生图标渐变 / 点缀色，保证跟 primary 同色系 */
export function deriveIconPalette(primaryColor?: string | null): IconPalette {
  const primary = normalizeHex(primaryColor) || DEFAULT_ICON_PRIMARY;
  const hsl = hexToHsl(primary) || { h: 186, s: 1, l: 0.5 };
  const { h, s, l } = hsl;

  // 浅底：同色系微调明度，色相只轻移一点增加层次（避免红→黄这种跨色相）
  const lightG1 = hslToHex(h, Math.min(1, s * 0.95), clamp01(Math.min(l, 0.58)));
  const lightG2 = hslToHex(h + 10, Math.min(1, s * 0.92), clamp01(Math.min(l, 0.5)));
  const lightG3 = hslToHex(h + 18, Math.min(1, s * 0.88), clamp01(Math.min(l + 0.06, 0.62)));
  const lightAccent = lightG1;
  const lightAccentAlt = mixHex(lightG3, '#FFFFFF', 0.15);
  const lightBadgeMark = hslToHex(h + 8, Math.min(1, s), clamp01(Math.min(l, 0.42)));

  // 深底：提亮以便在黑底上发光，仍锁在 primary 附近
  const darkBaseL = Math.max(l, 0.52);
  const darkG1 = hslToHex(h, Math.min(1, s * 0.92), clamp01(darkBaseL + 0.14));
  const darkG2 = hslToHex(h + 10, Math.min(1, s * 0.9), clamp01(darkBaseL));
  const darkG3 = hslToHex(h + 18, Math.min(1, s * 0.85), clamp01(darkBaseL + 0.1));
  const darkAccent = darkG1;
  const darkAccentAlt = mixHex(darkG3, '#FFFFFF', 0.2);
  const darkBadge1 = hslToHex(h + 12, Math.min(1, s), clamp01(Math.max(l, 0.48)));
  const darkBadge2 = hslToHex(h + 16, Math.min(1, s * 0.95), clamp01(Math.max(l * 0.72, 0.34)));
  const darkBadgeMark = mixHex(darkG1, '#FFFFFF', 0.4);

  return {
    primary,
    lightG1,
    lightG2,
    lightG3,
    lightAccent,
    lightAccentAlt,
    lightBadgeMark,
    lightShadow: mixHex(primary, '#1E1B4B', 0.55),
    darkG1,
    darkG2,
    darkG3,
    darkAccent,
    darkAccentAlt,
    darkBadge1,
    darkBadge2,
    darkBadgeMark,
  };
}

function svgDataUri(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/** 眼睛关于 cx 对称；嘴角明显低于眼底，避免挤在一起 */
function faceMarks(
  cx: number,
  cy: number,
  mode: 'light' | 'dark',
  palette: IconPalette,
  scale = 1,
): string {
  const eyeR = 2.0 * scale;
  const eyeGap = 4.7 * scale;
  const smileW = 3.55 * scale;
  const smileY = cy + 6.1 * scale;
  const smileQ = cy + 9.2 * scale;
  const left = mode === 'light' ? '#FFFFFF' : palette.darkAccent;
  const right = mode === 'light' ? '#FFFFFF' : palette.darkAccentAlt;
  return `
    <circle cx="${cx - eyeGap}" cy="${cy}" r="${eyeR}" fill="${left}"/>
    <circle cx="${cx + eyeGap}" cy="${cy}" r="${eyeR}" fill="${right}"/>
    <path d="M${cx - smileW} ${smileY} Q${cx} ${smileQ} ${cx + smileW} ${smileY}"
      stroke="#FFFFFF" stroke-width="${1.55 * scale}" stroke-linecap="round" fill="none"/>`;
}

/** AI搜片助手的五官基准（画面像素）：另两套按 parentScale 反算，保证大小/弧度/间距一致 */
const ASSISTANT_BODY_SCALE = 1.2;
const ASSISTANT_FACE_SCALE = 1.05;
const ASSISTANT_FACE_CY = 29.2;
const ASSISTANT_VISOR_CY = 31.9;

function faceLikeAssistant(
  mode: 'light' | 'dark',
  palette: IconPalette,
  parentScale: number,
  visorCy: number,
): string {
  const canvasFace = ASSISTANT_FACE_SCALE * ASSISTANT_BODY_SCALE;
  const canvasEyeOffset = (ASSISTANT_FACE_CY - ASSISTANT_VISOR_CY) * ASSISTANT_BODY_SCALE;
  return faceMarks(32, visorCy + canvasEyeOffset / parentScale, mode, palette, canvasFace / parentScale);
}

function scaleAround(s: number, content: string, cx = 32, cy = 32): string {
  return `<g transform="translate(${cx},${cy}) scale(${s}) translate(${-cx},${-cy})">${content}</g>`;
}

function visorLight(cx: number, cy: number, w = 21, h = 12.5): string {
  const x = cx - w / 2;
  const y = cy - h / 2;
  return `
    <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${h / 2}" fill="#0B1220"/>
    <rect x="${x + 0.55}" y="${y + 0.45}" width="${w - 1.1}" height="${h * 0.2}" rx="${h / 5}" fill="#FFFFFF" fill-opacity=".1"/>`;
}

/** 暗色内圈：无彩色描边，仅比外圈内侧更深，形成分层 */
function visorDark(cx: number, cy: number, w: number, h: number): string {
  return `<rect x="${cx - w / 2}" y="${cy - h / 2}" width="${w}" height="${h}" rx="${h / 2}" fill="#101018"/>`;
}

/** 光学居中的播放三角（几何中心会显得偏左） */
function playTriangle(cx: number, cy: number, fill: string): string {
  return `<path d="M${cx - 1.15} ${cy - 3.35} V${cy + 3.35} L${cx + 4.15} ${cy} Z" fill="${fill}"/>`;
}

function searchGlass(cx: number, cy: number, stroke: string): string {
  const lx = cx - 1.15;
  const ly = cy - 1.15;
  return `
    <circle cx="${lx}" cy="${ly}" r="3.25" stroke="${stroke}" stroke-width="1.85" fill="none"/>
    <path d="M${lx + 2.35} ${ly + 2.35} L${cx + 3.45} ${cy + 3.45}" stroke="${stroke}" stroke-width="1.85" stroke-linecap="round"/>`;
}

function lightDefs(id: string, palette: IconPalette): string {
  return `
    <defs>
      <linearGradient id="${id}-g" x1="10" y1="8" x2="54" y2="56" gradientUnits="userSpaceOnUse">
        <stop stop-color="${palette.lightG1}"/>
        <stop offset="0.52" stop-color="${palette.lightG2}"/>
        <stop offset="1" stop-color="${palette.lightG3}"/>
      </linearGradient>
      <linearGradient id="${id}-shine" x1="24" y1="10" x2="40" y2="48" gradientUnits="userSpaceOnUse">
        <stop stop-color="#FFFFFF" stop-opacity=".32"/>
        <stop offset="0.42" stop-color="#FFFFFF" stop-opacity="0"/>
      </linearGradient>
      <filter id="${id}-shadow" x="-25%" y="-25%" width="150%" height="150%">
        <feDropShadow dx="0" dy="1.4" stdDeviation="1.6" flood-color="${palette.lightShadow}" flood-opacity=".16"/>
      </filter>
      <filter id="${id}-badge" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="0.8" stdDeviation="0.7" flood-color="${palette.lightShadow}" flood-opacity=".22"/>
      </filter>
    </defs>`;
}

function darkDefs(id: string, palette: IconPalette): string {
  return `
    <defs>
      <linearGradient id="${id}-g" x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
        <stop stop-color="${palette.darkG1}"/>
        <stop offset="0.5" stop-color="${palette.darkG2}"/>
        <stop offset="1" stop-color="${palette.darkG3}"/>
      </linearGradient>
      <linearGradient id="${id}-badge" x1="38" y1="38" x2="56" y2="56" gradientUnits="userSpaceOnUse">
        <stop stop-color="${palette.darkBadge1}"/>
        <stop offset="1" stop-color="${palette.darkBadge2}"/>
      </linearGradient>
      <filter id="${id}-glow" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="0.95" result="b"/>
        <feMerge>
          <feMergeNode in="b"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
      <mask id="${id}-cut">
        <rect width="64" height="64" fill="#fff"/>
        <circle cx="46.5" cy="46.5" r="10.4" fill="#000"/>
      </mask>
    </defs>`;
}

function badgeLight(kind: 'search' | 'play', gid: string, palette: IconPalette): string {
  const ring = `
    <g filter="url(#${gid}-badge)">
      <circle cx="46.5" cy="46.5" r="8.6" fill="#FFFFFF" stroke="url(#${gid}-g)" stroke-width="1.85"/>
    </g>`;
  const mark = kind === 'search'
    ? searchGlass(46.5, 46.5, palette.lightBadgeMark)
    : playTriangle(46.7, 46.5, palette.lightBadgeMark);
  return ring + mark;
}

function badgeDark(kind: 'search' | 'play', gid: string, palette: IconPalette): string {
  const ring = `
    <circle cx="46.5" cy="46.5" r="8.6" fill="#0A0A12" stroke="url(#${gid}-badge)" stroke-width="1.7" filter="url(#${gid}-glow)"/>
    <circle cx="46.5" cy="46.5" r="8.6" fill="none" stroke="#FFFFFF" stroke-opacity=".18" stroke-width="0.5"/>`;
  const mark = kind === 'search'
    ? searchGlass(46.5, 46.5, palette.darkBadgeMark)
    : playTriangle(46.7, 46.5, palette.darkBadgeMark);
  return ring + mark;
}

const ROBOT_HEAD =
  'M25.2 18.8 H38.8 A8.2 8.2 0 0 1 47 27 V36.8 A8.2 8.2 0 0 1 38.8 45 H25.2 A8.2 8.2 0 0 1 17 36.8 V27 A8.2 8.2 0 0 1 25.2 18.8 Z';
const ROBOT_EAR_L = { x: 13.4, y: 28.2, w: 4.2, h: 8, rx: 2.1 };
const ROBOT_EAR_R = { x: 46.4, y: 28.2, w: 4.2, h: 8, rx: 2.1 };

function earRect(ear: { x: number; y: number; w: number; h: number; rx: number }, fill: string): string {
  return `<rect x="${ear.x}" y="${ear.y}" width="${ear.w}" height="${ear.h}" rx="${ear.rx}" fill="${fill}"/>`;
}
const BUBBLE =
  'M19.6 16.2 H44.4 A8.6 8.6 0 0 1 53 24.8 V38.4 A8.6 8.6 0 0 1 44.4 47 H31.8 C28.4 47 25.6 48.8 21.2 52.6 C22.6 49.6 23.6 48.2 24.4 47 H19.6 A8.6 8.6 0 0 1 11 38.4 V24.8 A8.6 8.6 0 0 1 19.6 16.2 Z';

const SPRITE_HEAD_CX = 32;
const SPRITE_HEAD_CY = 34.8;
const SPRITE_HEAD_R = 17.4;

function buildAiSearchAssistant(mode: 'light' | 'dark', palette: IconPalette): string {
  if (mode === 'light') {
    const body = `
      <rect x="30.6" y="11.2" width="2.8" height="8.2" rx="1.4" fill="url(#aL-g)"/>
      <circle cx="32" cy="11.2" r="2.15" fill="${palette.lightAccent}"/>
      ${earRect(ROBOT_EAR_L, 'url(#aL-g)')}
      ${earRect(ROBOT_EAR_R, 'url(#aL-g)')}
      <path d="${ROBOT_HEAD}" fill="url(#aL-g)"/>
      <path d="${ROBOT_HEAD}" fill="url(#aL-shine)"/>
      ${visorLight(32, 31.9, 25.6, 17.2)}
      ${faceLikeAssistant('light', palette, ASSISTANT_BODY_SCALE, ASSISTANT_VISOR_CY)}
    `;
    return svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  ${lightDefs('aL', palette)}
  <circle cx="32" cy="32" r="30.5" fill="#FFFFFF" filter="url(#aL-shadow)"/>
  ${scaleAround(1.2, body)}
  ${badgeLight('search', 'aL', palette)}
</svg>
`.trim());
  }
  const body = `
  <g stroke-linecap="round" stroke-linejoin="round">
    <g filter="url(#aD-glow)">
      <line x1="32" y1="12.4" x2="32" y2="18.8" stroke="url(#aD-g)" stroke-width="1.9" stroke-linecap="round"/>
      ${earRect(ROBOT_EAR_L, 'url(#aD-g)')}
      ${earRect(ROBOT_EAR_R, 'url(#aD-g)')}
      <path d="${ROBOT_HEAD}" fill="#07070F" stroke="url(#aD-g)" stroke-width="1.9"/>
    </g>
    ${visorDark(32, 31.9, 25.6, 17.2)}
    <path d="${ROBOT_HEAD}" fill="none" stroke="#FFFFFF" stroke-opacity=".14" stroke-width="0.45"/>
  </g>
  <circle cx="32" cy="11.6" r="2.05" fill="${palette.darkAccent}" filter="url(#aD-glow)"/>
  ${faceLikeAssistant('dark', palette, ASSISTANT_BODY_SCALE, ASSISTANT_VISOR_CY)}
`;
  return svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  ${darkDefs('aD', palette)}
  <circle cx="32" cy="32" r="30.5" fill="#07070F"/>
  ${scaleAround(1.2, body)}
  ${badgeDark('search', 'aD', palette)}
</svg>
`.trim());
}

function buildResourceNavigator(mode: 'light' | 'dark', palette: IconPalette): string {
  if (mode === 'light') {
    return svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  ${lightDefs('bL', palette)}
  <circle cx="32" cy="32" r="30.5" fill="#FFFFFF" filter="url(#bL-shadow)"/>
  ${scaleAround(1.0, `
    <path d="${BUBBLE}" fill="url(#bL-g)"/>
    <path d="${BUBBLE}" fill="url(#bL-shine)"/>
    ${visorLight(32, 31.6, 24.2, 15.2)}
    ${faceLikeAssistant('light', palette, 1.0, 31.6)}
  `)}
  ${badgeLight('play', 'bL', palette)}
</svg>
`.trim());
  }
  return svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  ${darkDefs('bD', palette)}
  <circle cx="32" cy="32" r="30.5" fill="#07070F"/>
  ${scaleAround(1.0, `
    <path d="${BUBBLE}" fill="#07070F" stroke="url(#bD-g)" stroke-width="1.9" stroke-linejoin="round" filter="url(#bD-glow)"/>
    <path d="${BUBBLE}" fill="none" stroke="#FFFFFF" stroke-opacity=".14" stroke-width="0.45" stroke-linejoin="round"/>
    ${visorDark(32, 31.6, 24.2, 15.2)}
    ${faceLikeAssistant('dark', palette, 1.0, 31.6)}
  `)}
  ${badgeDark('play', 'bD', palette)}
</svg>
`.trim());
}

function buildAiMovieSprite(mode: 'light' | 'dark', palette: IconPalette): string {
  if (mode === 'light') {
    return svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  ${lightDefs('cL', palette)}
  <circle cx="32" cy="32" r="30.5" fill="#FFFFFF" filter="url(#cL-shadow)"/>
  ${scaleAround(1.08, `
    <path d="M21.2 21.4 L14.8 12.6" stroke="${palette.lightAccent}" stroke-width="2.05" stroke-linecap="round"/>
    <path d="M42.8 21.4 L49.2 12.6" stroke="${palette.lightAccentAlt}" stroke-width="2.05" stroke-linecap="round"/>
    <circle cx="14.4" cy="12.2" r="2.0" fill="${palette.lightAccent}"/>
    <circle cx="49.6" cy="12.2" r="2.0" fill="${palette.lightAccentAlt}"/>
    <circle cx="${SPRITE_HEAD_CX}" cy="${SPRITE_HEAD_CY}" r="${SPRITE_HEAD_R}" fill="url(#cL-g)"/>
    <circle cx="${SPRITE_HEAD_CX}" cy="${SPRITE_HEAD_CY}" r="${SPRITE_HEAD_R}" fill="url(#cL-shine)"/>
    ${visorLight(32, 33.8, 23.2, 15.2)}
    ${faceLikeAssistant('light', palette, 1.08, 33.8)}
  `)}
  ${badgeLight('play', 'cL', palette)}
</svg>
`.trim());
  }
  return svgDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" fill="none">
  ${darkDefs('cD', palette)}
  <circle cx="32" cy="32" r="30.5" fill="#07070F"/>
  ${scaleAround(1.08, `
    <g filter="url(#cD-glow)" stroke="url(#cD-g)" stroke-width="1.9" stroke-linecap="round" fill="none">
      <path d="M21.2 21.6 L14.8 12.8"/>
      <path d="M42.8 21.6 L49.2 12.8"/>
    </g>
    <circle cx="${SPRITE_HEAD_CX}" cy="${SPRITE_HEAD_CY}" r="${SPRITE_HEAD_R}" fill="#07070F" stroke="url(#cD-g)" stroke-width="1.9" filter="url(#cD-glow)"/>
    <circle cx="${SPRITE_HEAD_CX}" cy="${SPRITE_HEAD_CY}" r="${SPRITE_HEAD_R}" fill="none" stroke="#FFFFFF" stroke-opacity=".14" stroke-width="0.45"/>
    <circle cx="32" cy="34.8" r="11.6" fill="#101018"/>
    <circle cx="14.6" cy="12.4" r="2.0" fill="${palette.darkAccent}" filter="url(#cD-glow)"/>
    <circle cx="49.4" cy="12.4" r="2.0" fill="${palette.darkAccentAlt}" filter="url(#cD-glow)"/>
    ${faceLikeAssistant('dark', palette, 1.08, 33.8)}
  `)}
  ${badgeDark('play', 'cD', palette)}
</svg>
`.trim());
}

type ImageIconBuilder = (mode: 'light' | 'dark', palette: IconPalette) => string;

const IMAGE_ICON_BUILDERS: Record<string, ImageIconBuilder> = {
  'ai-search-assistant': buildAiSearchAssistant,
  'resource-navigator': buildResourceNavigator,
  'ai-movie-sprite': buildAiMovieSprite,
};

export const DEFAULT_AGENT_ICON_ID = 'default';

/**
 * 默认 ◎ 的几何稿：双圆环（与字符 ◎ 一致），内圆直径 = 画布 45%。
 * 中心留空，头像/FAB 上透出主题色（#00E5FF），不要实心黑点。
 */
export const DEFAULT_GLYPH_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/>
  <circle cx="12" cy="12" r="5.4" fill="none" stroke="currentColor" stroke-width="2"/>
</svg>
`.trim();

/** 可选图标列表（配置 theme.icon 时从此处选择） */
export const AGENT_ICON_CATALOG: Record<string, AgentIconDefinition> = {
  default: {
    id: 'default',
    name: '默认',
    description: '青光束字符图标 ◎',
    kind: 'glyph',
    glyph: '◎',
  },
  'ai-search-assistant': {
    id: 'ai-search-assistant',
    name: 'AI搜片助手',
    description: '机器人表情 + 搜片徽标，助手属性与视频搜索最均衡（推荐）',
    kind: 'image',
  },
  'resource-navigator': {
    id: 'resource-navigator',
    name: '资源导航员',
    description: '对话气泡 + AI 表情 + 播放徽标，更像可交流的智能向导',
    kind: 'image',
  },
  'ai-movie-sprite': {
    id: 'ai-movie-sprite',
    name: 'AI寻片精灵',
    description: '双触角智能角色 + 播放徽标，活泼、年轻、记忆点更强',
    kind: 'image',
  },
};

export function listAgentIcons(): AgentIconDefinition[] {
  return Object.values(AGENT_ICON_CATALOG);
}

export function resolveAgentIcon(
  iconId?: string | null,
  lightBackground = false,
  primaryColor?: string | null,
): {
  id: string;
  kind: AgentIconKind;
  glyph: string;
  src: string | null;
  name: string;
} {
  const raw = (iconId || DEFAULT_AGENT_ICON_ID).trim() || DEFAULT_AGENT_ICON_ID;
  const def = AGENT_ICON_CATALOG[raw] || AGENT_ICON_CATALOG[DEFAULT_AGENT_ICON_ID];
  if (def.kind === 'image') {
    const builder = IMAGE_ICON_BUILDERS[def.id];
    const palette = deriveIconPalette(primaryColor);
    const src = builder
      ? builder(lightBackground ? 'light' : 'dark', palette)
      : null;
    return { id: def.id, kind: 'image', glyph: '◎', src, name: def.name };
  }
  return {
    id: def.id,
    kind: 'glyph',
    glyph: def.glyph || '◎',
    src: null,
    name: def.name,
  };
}
